import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    SITE_NAME,
    SITE_TITLE,
    SITE_URL,
    DEFAULT_KEYWORDS,
    DEFAULT_OG_IMAGE,
    ORG_NAME,
    ROUTE_SEO,
    NOINDEX_PATHS,
    canonicalUrl
} from './config';

const upsertMeta = (selector, attributes) => {
    let element = document.head.querySelector(selector);
    if (!element) {
        element = document.createElement('meta');
        document.head.appendChild(element);
    }
    Object.entries(attributes).forEach(([key, value]) => {
        if (value == null || value === '') {
            element.removeAttribute(key);
        } else {
            element.setAttribute(key, value);
        }
    });
    return element;
};

const upsertLink = (rel, href) => {
    let element = document.head.querySelector(`link[rel="${rel}"]`);
    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
    }
    element.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
    let script = document.getElementById(id);
    if (!data) {
        if (script) script.remove();
        return;
    }
    if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
};

const SeoHead = ({ title, description, keywords, image, noindex, type = 'website' }) => {
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const routeMeta = ROUTE_SEO[location.pathname] || {};
    const isNoIndex =
        noindex ||
        NOINDEX_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));

    const translatedTitle = routeMeta.ns ? t(`${routeMeta.ns}:pageTitle`, { defaultValue: '' }) : '';
    const translatedDescription = routeMeta.ns ? t(`${routeMeta.ns}:pageDescription`, { defaultValue: '' }) : '';
    const pageTitle = title || translatedTitle || SITE_TITLE;
    const fullTitle = pageTitle === SITE_TITLE || pageTitle.includes('|') ? pageTitle : `${pageTitle} | ${SITE_NAME} LHRC`;
    const pageDescription =
        description ||
        translatedDescription ||
        'Katiba database of the Legal and Human Rights Centre: Tanzania constitution, laws, cases, reports, and human rights materials.';
    const pageKeywords = keywords || routeMeta.keywords || DEFAULT_KEYWORDS;
    const canonical = canonicalUrl(location.pathname);
    const ogImage = image || DEFAULT_OG_IMAGE;
    const locale = i18n.language === 'sw' ? 'sw_TZ' : 'en_US';

    useEffect(() => {
        document.title = fullTitle;
        document.documentElement.lang = i18n.language === 'sw' ? 'sw' : 'en';

        upsertMeta('meta[name="description"]', { name: 'description', content: pageDescription });
        upsertMeta('meta[name="keywords"]', { name: 'keywords', content: pageKeywords });
        upsertMeta('meta[name="author"]', { name: 'author', content: ORG_NAME });
        upsertMeta('meta[name="robots"]', {
            name: 'robots',
            content: isNoIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large'
        });
        upsertMeta('meta[name="googlebot"]', {
            name: 'googlebot',
            content: isNoIndex ? 'noindex, nofollow' : 'index, follow'
        });

        upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
        upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
        upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
        upsertMeta('meta[property="og:description"]', { property: 'og:description', content: pageDescription });
        upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
        upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });
        upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: locale });
        upsertMeta('meta[property="og:locale:alternate"]', {
            property: 'og:locale:alternate',
            content: locale === 'sw_TZ' ? 'en_US' : 'sw_TZ'
        });

        upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
        upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
        upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: pageDescription });
        upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

        upsertLink('canonical', canonical);
        upsertLink('alternate', canonical);

        setJsonLd('katiba-org-jsonld', {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: ORG_NAME,
            alternateName: ['LHRC', 'Katiba Database'],
            url: SITE_URL,
            logo: DEFAULT_OG_IMAGE,
            email: 'lhrc@humanrights.or.tz',
            sameAs: [
                'https://www.humanrights.or.tz',
                'https://www.facebook.com/humanrightstz',
                'https://www.twitter.com/humanrightstz',
                'https://www.youtube.com/@hakitv1216'
            ]
        });

        setJsonLd('katiba-website-jsonld', {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: SITE_TITLE,
            url: SITE_URL,
            inLanguage: ['en', 'sw'],
            publisher: { '@type': 'Organization', name: ORG_NAME },
            potentialAction: {
                '@type': 'SearchAction',
                target: `${SITE_URL}/search?q={search_term_string}`,
                'query-input': 'required name=search_term_string'
            }
        });
    }, [fullTitle, pageDescription, pageKeywords, canonical, ogImage, locale, isNoIndex, type, i18n.language]);

    return null;
};

export default SeoHead;
